import React, { useState, forwardRef, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  GoogleAuthProvider, 
  FacebookAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../firebase';
import '../styles/AuthModule.css';

const AuthModule = forwardRef((props, ref) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [ageVerified, setAgeVerified] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  
  const isInstagramBrowser = () => {
    const userAgent = navigator.userAgent || '';
    return userAgent.indexOf('Instagram') > -1;
  };
  
  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          console.log("Redirect sign-in successful");
        }
      } catch (error) {
        if (error.code === 'auth/disallowed-useragent') {
          setError('For security reasons, this browser is not supported for authentication. Please open in your default browser.');
        } else {
          setError(error.message);
        }
      }
    };
    
    checkRedirectResult();
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // For signup, enforce age verification and terms agreement
    if (isSignUp && (!ageVerified || !termsAgreed)) {
      setError('You must confirm your age and agree to the terms to continue');
      return;
    }
    
    try {
      if (isSignUp) {
        await createUserWithEmailAndPassword(auth, email, password);
        // Store consent in localStorage for record-keeping
        localStorage.setItem('userConsent', JSON.stringify({
          ageVerified: ageVerified,
          termsAgreed: termsAgreed,
          timestamp: new Date().toISOString()
        }));
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (error) {
      setError(error.message);
    }
  };
  
  const handleGoogleSignIn = async () => {
    // For Google sign-in, only require age verification and terms agreement for new users
    if (isSignUp && (!ageVerified || !termsAgreed)) {
      setError('You must confirm your age and agree to the terms to continue');
      return;
    }
    
    try {
      const provider = new GoogleAuthProvider();
      
      if (isInstagramBrowser()) {
        await signInWithRedirect(auth, provider);
        return;
      }
      
      const result = await signInWithPopup(auth, provider);
      console.log("Google sign-in successful");
      
      // If this is a new user (sign up), store the consent
      if (result._tokenResponse?.isNewUser && isSignUp) {
        localStorage.setItem('userConsent', JSON.stringify({
          ageVerified: ageVerified,
          termsAgreed: termsAgreed,
          timestamp: new Date().toISOString()
        }));
      }
    } catch (error) {
      if (error.code === 'auth/disallowed-useragent') {
        setError('For security reasons, this browser is not supported for authentication. Please open in your default browser.');
      } else {
        setError(error.message);
      }
    }
  };
  
  const handleFacebookSignIn = async () => {
    // For Facebook sign-in, only require age verification and terms agreement for new users
    if (isSignUp && (!ageVerified || !termsAgreed)) {
      setError('You must confirm your age and agree to the terms to continue');
      return;
    }
    
    try {
      const provider = new FacebookAuthProvider();
      // Add custom OAuth redirect URI for Facebook
      provider.setCustomParameters({
        redirect_uri: 'https://sunnysideupstorys-4030a.firebaseapp.com/__/auth/handler'
      });
      
      if (isInstagramBrowser()) {
        await signInWithRedirect(auth, provider);
        return;
      }
      
      const result = await signInWithPopup(auth, provider);
      console.log("Facebook sign-in successful");
      
      // If this is a new user (sign up), store the consent
      if (result._tokenResponse?.isNewUser && isSignUp) {
        localStorage.setItem('userConsent', JSON.stringify({
          ageVerified: ageVerified,
          termsAgreed: termsAgreed,
          timestamp: new Date().toISOString()
        }));
      }
    } catch (error) {
      if (error.code === 'auth/disallowed-useragent') {
        setError('For security reasons, this browser is not supported for authentication. Please open in your default browser.');
      } else {
        setError(error.message);
      }
    }
  };
  
  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    
    // Validate email format client-side
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      await sendPasswordResetEmail(auth, email);
      // Always show success message regardless of whether the email exists
      // Firebase will only send an email if the account exists
      setResetSent(true);
      setError('');
    } catch (error) {
      // Only show errors for invalid email format or network issues
      if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else {
        setError('Could not send reset email. Please try again later.');
      }
    }
  };
  
  const toggleTerms = () => {
    setShowTerms(!showTerms);
  };
  
  return (
    <div className="auth-module" ref={ref}>
      {isInstagramBrowser() && (
        <div className="instagram-browser-warning">
          <p>Instagram's browser doesn't support secure login.</p>
          <a 
            href={`https://${window.location.hostname}${window.location.pathname}`}
            target="_blank"
            rel="noopener noreferrer"
            className="open-in-browser-button"
          >
            Open in Default Browser
          </a>
        </div>
      )}
      <h2>{isSignUp ? 'Sign Up' : 'Sign In'}</h2>
      <p className="auth-message">Continue reading this story by signing in</p>
      
      {error && <p className="auth-error">{error}</p>}
      {resetSent && <p className="auth-success">If an account exists with this email, a password reset link will be sent shortly. Please check your inbox.</p>}
      
      <button 
        className="google-button"
        onClick={handleGoogleSignIn}
      >
        <svg className="google-icon" width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
        Continue with Google
      </button>
      
      <button 
        className="facebook-button"
        onClick={handleFacebookSignIn}
      >
        <svg className="facebook-icon" width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
          <path fill="#3B5998" d="M44.5 20H32v-8c0-1.1.9-2 2-2h8V0h-8C24.7 0 20 4.7 20 10.5V20H12v10h8v28h12V30h8.5l4-10z"/>
        </svg>
        Continue with Facebook
      </button>
      
      <div className="auth-divider">
        <span>or</span>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        
        {isSignUp && (
          <div className="consent-wrapper">
            <div className="consent-checkboxes">
              <div className="consent-item">
                <input
                  type="checkbox"
                  id="age-verification"
                  checked={ageVerified}
                  onChange={() => setAgeVerified(!ageVerified)}
                />
                <label htmlFor="age-verification"><span className="required-indicator">*</span>I confirm that I am at least 18 years old </label>
              </div>
              
              <div className="consent-item">
                <input
                  type="checkbox"
                  id="terms-agreement"
                  checked={termsAgreed}
                  onChange={() => setTermsAgreed(!termsAgreed)}
                />
                <label htmlFor="terms-agreement">
                  <span className="required-indicator">*</span> I agree to the <a href="/privacy" className="terms-link" target="_blank" rel="noopener noreferrer">Terms of Service and Privacy Policy</a>
                </label>
              </div>
            </div>
          </div>
        )}
        
        {!isSignUp && (
          <div className="privacy-reminder">
            By signing in, you acknowledge that you have read and agreed to our <a href="/privacy" className="terms-link" target="_blank" rel="noopener noreferrer">Terms of Service and Privacy Policy</a>.
          </div>
        )}
        
        {showTerms && (
          <div className="terms-popup">
            <div className="terms-content">
              <h3>Terms of Service & Privacy Policy</h3>
              <div className="terms-text">
                <p>By using Sunny Sideup Storys, you agree to:</p>
                <ul>
                  <li>Use content for personal, non-commercial purposes only</li>
                  <li>Not reproduce, distribute, or sell any content</li>
                  <li>Respect intellectual property rights of all content</li>
                  <li>Not share your account credentials with others</li>
                </ul>
                <p>Our Privacy Policy explains how we collect and use your data, including:</p>
                <ul>
                  <li>Email and account information for authentication</li>
                  <li>Reading history and preferences to personalize your experience</li>
                  <li>Cookies for functionality and analytics</li>
                </ul>
                <p>We do not sell your personal information to third parties.</p>
              </div>
              <button type="button" className="close-terms" onClick={toggleTerms}>Close</button>
            </div>
          </div>
        )}
        
        {!isSignUp && (
          <div className="forgot-password">
            <button 
              type="button" 
              className="forgot-button"
              onClick={handleForgotPassword}
            >
              Forgot password?
            </button>
          </div>
        )}
        
        <button type="submit" className="auth-button">
          {isSignUp ? 'Sign Up' : 'Sign In'}
        </button>
      </form>
      
      <p className="auth-toggle">
        {isSignUp ? 'Already have an account? ' : 'Need an account? '}
        <button 
          className="toggle-button"
          onClick={() => setIsSignUp(!isSignUp)}
        >
          {isSignUp ? 'Sign In' : 'Sign Up'}
        </button>
      </p>
    </div>
  );
});

export default AuthModule; 